import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Play, Pause, RotateCcw, Settings, Volume2, VolumeX, ChevronDown } from 'lucide-react';
import { ToolSEOWrapper } from '../components/tools/ToolSEOWrapper';

// Timer Types Configuration
const timerTypes = {
  pomodoro: {
    name: 'Pomodoro',
    description: 'Focus sessions with breaks',
    work: 25 * 60,
    shortBreak: 5 * 60,
    longBreak: 15 * 60,
    rounds: 4,
    color: '#E63946'
  },
  meditation: {
    name: 'Meditation',
    description: 'Guided breathing & stillness',
    durations: [5, 10, 15, 20, 30],
    color: '#00F0FF'
  },
  breathwork: {
    name: 'Breathwork',
    description: 'Box breathing & Wim Hof',
    patterns: {
      box: { name: 'Box Breathing', inhale: 4, hold1: 4, exhale: 4, hold2: 4 },
      relaxing: { name: '4-7-8 Relaxing', inhale: 4, hold1: 7, exhale: 8, hold2: 0 },
      energizing: { name: 'Power Breath', inhale: 2, hold1: 0, exhale: 2, hold2: 0 }
    },
    color: '#00F0FF'
  },
  coldExposure: {
    name: 'Cold Exposure',
    description: 'Ice bath & cold shower timer',
    presets: [
      { name: 'Beginner', duration: 30 },
      { name: 'Intermediate', duration: 120 },
      { name: 'Advanced', duration: 300 },
      { name: 'Wim Hof', duration: 600 }
    ],
    color: '#00F0FF'
  }
};

// Format time as MM:SS
const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// Circular Progress Component
const CircularProgress = ({ progress, size = 300, strokeWidth = 8, color }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (progress * circumference);

  return (
    <svg width={size} height={size} className="transform -rotate-90">
      {/* Background Circle */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="#262626"
        strokeWidth={strokeWidth}
      />
      {/* Progress Circle */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        className="transition-all duration-1000 ease-linear"
      />
    </svg>
  );
};

// Pomodoro Timer Component
const PomodoroTimer = () => {
  const [phase, setPhase] = useState('work'); // work, shortBreak, longBreak
  const [round, setRound] = useState(1);
  const [timeLeft, setTimeLeft] = useState(timerTypes.pomodoro.work);
  const [isRunning, setIsRunning] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const intervalRef = useRef(null);

  const getPhaseTime = () => {
    switch (phase) {
      case 'shortBreak': return timerTypes.pomodoro.shortBreak;
      case 'longBreak': return timerTypes.pomodoro.longBreak;
      default: return timerTypes.pomodoro.work;
    }
  };

  const playSound = useCallback(() => {
    if (soundEnabled) {
      const audio = new Audio('data:audio/wav;base64,UklGRigAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQQAAAAAAA==');
      audio.play().catch(() => {});
    }
  }, [soundEnabled]);

  useEffect(() => {
    if (isRunning && timeLeft > 0) {
      intervalRef.current = setInterval(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      playSound();
      // Auto-advance phases
      if (phase === 'work') {
        if (round >= timerTypes.pomodoro.rounds) {
          setPhase('longBreak');
          setTimeLeft(timerTypes.pomodoro.longBreak);
          setRound(1);
        } else {
          setPhase('shortBreak');
          setTimeLeft(timerTypes.pomodoro.shortBreak);
        }
      } else {
        setPhase('work');
        setTimeLeft(timerTypes.pomodoro.work);
        if (phase === 'shortBreak') setRound(prev => prev + 1);
      }
      setIsRunning(false);
    }
    return () => clearInterval(intervalRef.current);
  }, [isRunning, timeLeft, phase, round, playSound]);

  const reset = () => {
    setIsRunning(false);
    setPhase('work');
    setRound(1);
    setTimeLeft(timerTypes.pomodoro.work);
  };

  const progress = 1 - (timeLeft / getPhaseTime());
  const phaseLabel = phase === 'work' ? 'Focus' : phase === 'shortBreak' ? 'Short Break' : 'Long Break';

  return (
    <div className="flex flex-col items-center">
      {/* Timer Display */}
      <div className="relative mb-8">
        <CircularProgress 
          progress={progress} 
          size={280} 
          color={phase === 'work' ? '#E63946' : '#00F0FF'} 
        />
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-xs uppercase tracking-wider text-zinc-500 mb-2">
            {phaseLabel}
          </span>
          <span 
            className="text-6xl text-white"
            style={{ fontFamily: 'JetBrains Mono, monospace' }}
          >
            {formatTime(timeLeft)}
          </span>
          <span className="text-sm text-zinc-500 mt-2">
            Round {round}/{timerTypes.pomodoro.rounds}
          </span>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => setIsRunning(!isRunning)}
          className={`w-16 h-16 rounded-full flex items-center justify-center transition-all duration-300 ${
            isRunning 
              ? 'bg-zinc-800 text-white' 
              : 'bg-[#E63946] text-white hover:bg-red-700'
          }`}
          data-testid="pomodoro-toggle"
        >
          {isRunning ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-1" />}
        </button>
        <button
          onClick={reset}
          className="w-12 h-12 rounded-full bg-zinc-900 text-zinc-400 hover:text-white flex items-center justify-center transition-colors duration-300"
          data-testid="pomodoro-reset"
        >
          <RotateCcw className="w-5 h-5" />
        </button>
        <button
          onClick={() => setSoundEnabled(!soundEnabled)}
          className="w-12 h-12 rounded-full bg-zinc-900 text-zinc-400 hover:text-white flex items-center justify-center transition-colors duration-300"
        >
          {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
        </button>
      </div>
    </div>
  );
};

// Meditation Timer Component
const MeditationTimer = () => {
  const [duration, setDuration] = useState(10);
  const [timeLeft, setTimeLeft] = useState(10 * 60);
  const [isRunning, setIsRunning] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    setTimeLeft(duration * 60);
  }, [duration]);

  useEffect(() => {
    if (isRunning && timeLeft > 0) {
      intervalRef.current = setInterval(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      setIsRunning(false);
    }
    return () => clearInterval(intervalRef.current);
  }, [isRunning, timeLeft]);

  const progress = 1 - (timeLeft / (duration * 60));

  return (
    <div className="flex flex-col items-center">
      {/* Duration Selector */}
      <div className="flex gap-2 mb-8">
        {timerTypes.meditation.durations.map((mins) => (
          <button
            key={mins}
            onClick={() => { setDuration(mins); setTimeLeft(mins * 60); setIsRunning(false); }}
            className={`px-4 py-2 text-sm uppercase tracking-wider transition-all duration-300 ${
              duration === mins
                ? 'bg-[#00F0FF] text-black'
                : 'bg-zinc-900 text-zinc-400 hover:text-white'
            }`}
            style={{ fontFamily: 'Bebas Neue, sans-serif' }}
          >
            {mins}min
          </button>
        ))}
      </div>

      {/* Timer Display */}
      <div className="relative mb-8">
        <CircularProgress progress={progress} size={280} color="#00F0FF" />
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-xs uppercase tracking-wider text-zinc-500 mb-2">
            {isRunning ? 'Breathe' : 'Ready'}
          </span>
          <span 
            className="text-6xl text-white"
            style={{ fontFamily: 'JetBrains Mono, monospace' }}
          >
            {formatTime(timeLeft)}
          </span>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => setIsRunning(!isRunning)}
          className={`w-16 h-16 rounded-full flex items-center justify-center transition-all duration-300 ${
            isRunning 
              ? 'bg-zinc-800 text-white' 
              : 'bg-[#00F0FF] text-black hover:bg-cyan-400'
          }`}
          data-testid="meditation-toggle"
        >
          {isRunning ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-1" />}
        </button>
        <button
          onClick={() => { setTimeLeft(duration * 60); setIsRunning(false); }}
          className="w-12 h-12 rounded-full bg-zinc-900 text-zinc-400 hover:text-white flex items-center justify-center transition-colors duration-300"
        >
          <RotateCcw className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

// Breathwork Timer Component
const BreathworkTimer = () => {
  const [pattern, setPattern] = useState('box');
  const [phase, setPhase] = useState('inhale');
  const [isRunning, setIsRunning] = useState(false);
  const [cycles, setCycles] = useState(0);
  const [countdown, setCountdown] = useState(4);
  const intervalRef = useRef(null);

  const currentPattern = timerTypes.breathwork.patterns[pattern];
  const phases = ['inhale', 'hold1', 'exhale', 'hold2'].filter(p => currentPattern[p] > 0);

  useEffect(() => {
    if (isRunning) {
      const currentDuration = currentPattern[phase];
      setCountdown(currentDuration);
      
      intervalRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            // Move to next phase
            const currentIndex = phases.indexOf(phase);
            const nextIndex = (currentIndex + 1) % phases.length;
            const nextPhase = phases[nextIndex];
            
            if (nextIndex === 0) {
              setCycles(c => c + 1);
            }
            
            setPhase(nextPhase);
            return currentPattern[nextPhase];
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(intervalRef.current);
  }, [isRunning, phase, pattern, currentPattern, phases]);

  const getPhaseLabel = () => {
    switch (phase) {
      case 'inhale': return 'Inhale';
      case 'hold1': return 'Hold';
      case 'exhale': return 'Exhale';
      case 'hold2': return 'Hold';
      default: return '';
    }
  };

  const reset = () => {
    setIsRunning(false);
    setPhase('inhale');
    setCycles(0);
    setCountdown(currentPattern.inhale);
  };

  return (
    <div className="flex flex-col items-center">
      {/* Pattern Selector */}
      <div className="flex flex-wrap justify-center gap-2 mb-8">
        {Object.entries(timerTypes.breathwork.patterns).map(([key, p]) => (
          <button
            key={key}
            onClick={() => { setPattern(key); reset(); }}
            className={`px-4 py-2 text-sm uppercase tracking-wider transition-all duration-300 ${
              pattern === key
                ? 'bg-[#00F0FF] text-black'
                : 'bg-zinc-900 text-zinc-400 hover:text-white'
            }`}
            style={{ fontFamily: 'Bebas Neue, sans-serif' }}
          >
            {p.name}
          </button>
        ))}
      </div>

      {/* Breathing Animation */}
      <div className="relative mb-8">
        <div 
          className={`w-64 h-64 rounded-full border-4 border-[#00F0FF] flex items-center justify-center transition-transform duration-1000 ${
            phase === 'inhale' ? 'scale-110' : phase === 'exhale' ? 'scale-90' : 'scale-100'
          }`}
          style={{ 
            boxShadow: isRunning ? '0 0 60px rgba(0, 240, 255, 0.3)' : 'none'
          }}
        >
          <div className="text-center">
            <span 
              className="block text-5xl text-white mb-2"
              style={{ fontFamily: 'JetBrains Mono, monospace' }}
            >
              {countdown}
            </span>
            <span 
              className="text-xl text-[#00F0FF] uppercase tracking-wider"
              style={{ fontFamily: 'Bebas Neue, sans-serif' }}
            >
              {getPhaseLabel()}
            </span>
          </div>
        </div>
      </div>

      {/* Cycles Counter */}
      <div className="text-zinc-500 text-sm mb-6">
        Cycles completed: <span className="text-white">{cycles}</span>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => setIsRunning(!isRunning)}
          className={`w-16 h-16 rounded-full flex items-center justify-center transition-all duration-300 ${
            isRunning 
              ? 'bg-zinc-800 text-white' 
              : 'bg-[#00F0FF] text-black hover:bg-cyan-400'
          }`}
          data-testid="breathwork-toggle"
        >
          {isRunning ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-1" />}
        </button>
        <button
          onClick={reset}
          className="w-12 h-12 rounded-full bg-zinc-900 text-zinc-400 hover:text-white flex items-center justify-center transition-colors duration-300"
        >
          <RotateCcw className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

// Cold Exposure Timer Component
const ColdExposureTimer = () => {
  const [preset, setPreset] = useState(timerTypes.coldExposure.presets[0]);
  const [timeLeft, setTimeLeft] = useState(preset.duration);
  const [isRunning, setIsRunning] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    setTimeLeft(preset.duration);
  }, [preset]);

  useEffect(() => {
    if (isRunning && timeLeft > 0) {
      intervalRef.current = setInterval(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      setIsRunning(false);
    }
    return () => clearInterval(intervalRef.current);
  }, [isRunning, timeLeft]);

  const progress = 1 - (timeLeft / preset.duration);

  return (
    <div className="flex flex-col items-center">
      {/* Preset Selector */}
      <div className="flex flex-wrap justify-center gap-2 mb-8">
        {timerTypes.coldExposure.presets.map((p) => (
          <button
            key={p.name}
            onClick={() => { setPreset(p); setTimeLeft(p.duration); setIsRunning(false); }}
            className={`px-4 py-2 text-sm uppercase tracking-wider transition-all duration-300 ${
              preset.name === p.name
                ? 'bg-[#00F0FF] text-black'
                : 'bg-zinc-900 text-zinc-400 hover:text-white'
            }`}
            style={{ fontFamily: 'Bebas Neue, sans-serif' }}
          >
            {p.name}
          </button>
        ))}
      </div>

      {/* Timer Display */}
      <div className="relative mb-8">
        <CircularProgress progress={progress} size={280} color="#00F0FF" />
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-xs uppercase tracking-wider text-zinc-500 mb-2">
            {isRunning ? 'Stay Strong' : preset.name}
          </span>
          <span 
            className="text-6xl text-white"
            style={{ fontFamily: 'JetBrains Mono, monospace' }}
          >
            {formatTime(timeLeft)}
          </span>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => setIsRunning(!isRunning)}
          className={`w-16 h-16 rounded-full flex items-center justify-center transition-all duration-300 ${
            isRunning 
              ? 'bg-zinc-800 text-white' 
              : 'bg-[#00F0FF] text-black hover:bg-cyan-400'
          }`}
          data-testid="cold-toggle"
        >
          {isRunning ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-1" />}
        </button>
        <button
          onClick={() => { setTimeLeft(preset.duration); setIsRunning(false); }}
          className="w-12 h-12 rounded-full bg-zinc-900 text-zinc-400 hover:text-white flex items-center justify-center transition-colors duration-300"
        >
          <RotateCcw className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export default function TimersPage() {
  const [activeTimer, setActiveTimer] = useState('pomodoro');

  const timers = {
    pomodoro: <PomodoroTimer />,
    meditation: <MeditationTimer />,
    breathwork: <BreathworkTimer />,
    coldExposure: <ColdExposureTimer />
  };

  return (
    <ToolSEOWrapper toolKey="timers">
    <main className="pt-20 min-h-screen" data-testid="timers-page">
      {/* Hero Section */}
      <section className="container-ninja section-spacing">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-ninja-label text-[#00F0FF] mb-4">Performance Tools</p>
          <h1 className="text-white mb-6" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
            Train Your <span className="text-gradient-bio">Discipline</span>
          </h1>
          <p className="text-zinc-400 text-lg leading-relaxed">
            Interactive tools to structure your practice. From focused work sessions to guided breathwork.
          </p>
        </div>
      </section>

      {/* Timer Selection */}
      <section className="container-ninja pb-8">
        <div className="flex flex-wrap justify-center gap-4">
          {Object.entries(timerTypes).map(([key, timer]) => (
            <button
              key={key}
              onClick={() => setActiveTimer(key)}
              className={`px-6 py-4 transition-all duration-300 ${
                activeTimer === key
                  ? 'bg-zinc-800 border border-zinc-700'
                  : 'bg-zinc-900/50 border border-transparent hover:border-zinc-800'
              }`}
              data-testid={`timer-tab-${key}`}
            >
              <span 
                className={`block text-lg mb-1 ${activeTimer === key ? 'text-white' : 'text-zinc-400'}`}
                style={{ fontFamily: 'Bebas Neue, sans-serif' }}
              >
                {timer.name}
              </span>
              <span className="block text-xs text-zinc-600">
                {timer.description}
              </span>
            </button>
          ))}
        </div>
      </section>

      {/* Active Timer */}
      <section className="container-ninja pb-24">
        <div className="max-w-lg mx-auto glass-panel p-8 md:p-12">
          {timers[activeTimer]}
        </div>
      </section>

      {/* Tips Section */}
      <section className="bg-zinc-900/50 py-16">
        <div className="container-ninja">
          <h2 
            className="text-white text-2xl text-center mb-12"
            style={{ fontFamily: 'Bebas Neue, sans-serif' }}
          >
            Pro Tips
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'Consistency Beats Intensity',
                tip: 'Short daily practice is better than occasional long sessions. Build the habit first.'
              },
              {
                title: 'Track Your Progress',
                tip: 'Keep a log of your sessions. Notice patterns in energy, focus, and mood.'
              },
              {
                title: 'Stack Your Practices',
                tip: 'Combine breathwork before meditation, or cold exposure after workouts for synergistic effects.'
              }
            ].map((item) => (
              <div key={item.title} className="text-center p-6">
                <h3 
                  className="text-white text-lg mb-3"
                  style={{ fontFamily: 'Bebas Neue, sans-serif' }}
                >
                  {item.title}
                </h3>
                <p className="text-zinc-500 text-sm">{item.tip}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
    </ToolSEOWrapper>
  );
}
